import React from 'react';
import { ThemePresetCard, type ThemePreset } from './ThemePresetCard';

export interface ThemePresetPickerProps {
  presets: ThemePreset[];
  activePresetId: string | null;
  onPick: (preset: ThemePreset) => void;
  /** Optional action button (e.g. "New Preset") rendered next to the header. */
  headerAction?: React.ReactNode;
  /** Override the section title. */
  title?: string;
  /** Override the section description. */
  description?: string;
  /**
   * AC-Design-1: When true, suppress the section header entirely (title,
   * description, headerAction). Used by the org Design tab where a single
   * merged header sits in the sticky save bar above.
   */
  hideHeader?: boolean;
  /** Optional callbacks — only wired on the admin page where CRUD is allowed. */
  onDelete?: (presetId: string) => void;
  onDuplicate?: (preset: ThemePreset) => void;
  /** Hide duplicate/delete overlays on every card (read-only for org admins). */
  readOnly?: boolean;
}

/**
 * Shared preset gallery — used by both the super-admin DesignSystem page
 * (with full CRUD) and the org-scoped System Settings tab (read-only pick).
 */
export function ThemePresetPicker({
  presets,
  activePresetId,
  onPick,
  headerAction,
  title = 'Theme Presets',
  description = 'Click a preset to load it into the editor. Save to persist changes.',
  hideHeader = false,
  onDelete,
  onDuplicate,
  readOnly,
}: ThemePresetPickerProps) {
  return (
    <div className="max-w-[1400px] mx-auto px-6 pt-6 pb-2">
      {/* AC-Design-1: header is suppressed on the org Design tab where a
          single merged header sits in the sticky save bar above. The super-
          admin /admin/design-system page still renders the header normally. */}
      {!hideHeader && (
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider">{title}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
          </div>
          {headerAction}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="theme-preset-grid">
        {presets.map((preset) => (
          <ThemePresetCard
            key={preset.id}
            preset={preset}
            isActive={preset.id === activePresetId}
            onEdit={onPick}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
            hideActions={readOnly}
          />
        ))}
      </div>
    </div>
  );
}
